import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';

import { durationLabel, endLabel, floorToSlot, slotDateTime } from '../event-time';

export interface DurationPreset { label: string; hours: number }

/**
 * The Runs for sheet's content (plan §6). Tapping a preset picks it and
 * closes; "Pick an end time" expands inline and is confirmed with the sheet's
 * Done (the parent reads `customOpen` / `customValid` and calls confirmCustom()).
 *
 * A duration that is not a preset — voice heard "3 to 6", or a restored draft —
 * shows as its own ticked row at the top.
 */
@Component({
  selector: 'app-duration-sheet',
  templateUrl: './duration-sheet.component.html',
  styleUrls: ['./duration-sheet.component.scss'],
})
export class DurationSheetComponent implements OnInit {
  @Input() startDate = '';
  @Input() startTime = '';
  /** Current duration in hours; null while a custom end is set. */
  @Input() durationHours: number | null = null;
  @Input() endDate = '';
  @Input() endTime = '';
  @Input() presets: DurationPreset[] = [];
  @Input() timeOptions: string[] = [];

  @Output() pickedPreset = new EventEmitter<number>();
  @Output() pickedCustom = new EventEmitter<{ endDate: string; endTime: string }>();
  /** So the sheet can show Done only while the custom end is open. */
  @Output() customOpenChange = new EventEmitter<boolean>();

  customOpen = false;
  customDate = '';
  customTime = '';

  ngOnInit(): void {
    this.customOpen = this.durationHours === null;
    this.customDate = this.endDate;
    this.customTime = this.endTime;
  }

  /** The current duration when it is not one of the presets. */
  get currentExtra(): DurationPreset | null {
    const h = this.durationHours;
    if (h === null || this.presets.some(p => p.hours === h)) return null;
    return { label: durationLabel(h), hours: h };
  }

  endsFor(hours: number): string {
    const start = slotDateTime(this.startDate, this.startTime);
    if (!start) return '';
    const end = start.plus({ hours });
    return `Ends ${end.toFormat('ccc d LLL')}, ${floorToSlot(end)}`;
  }

  isSelected(hours: number): boolean {
    return !this.customOpen && this.durationHours === hours;
  }

  pick(hours: number): void {
    this.pickedPreset.emit(hours);
  }

  toggleCustom(): void {
    this.customOpen = !this.customOpen;
    this.customOpenChange.emit(this.customOpen);
  }

  /** Why the custom end can't be used, or '' when it can. Same rules as the form validator. */
  get customError(): string {
    const start = slotDateTime(this.startDate, this.startTime);
    const end = slotDateTime(this.customDate, this.customTime);
    if (!start || !end) return 'Pick an end date and time.';
    if (end <= start) return 'The end has to come after the start.';
    if (end.diff(start, 'days').days > 10) return 'An event can run for at most 10 days.';
    return '';
  }

  get customValid(): boolean {
    return !this.customError;
  }

  /** "Runs 5 hours · ends 1:15 AM", under the custom controls. */
  get customSummary(): string {
    const start = slotDateTime(this.startDate, this.startTime);
    const end = slotDateTime(this.customDate, this.customTime);
    if (!start || !end || !this.customValid) return '';
    return `Runs ${durationLabel(end.diff(start, 'hours').hours)} · ends ${endLabel(start, end)}`;
  }

  /** Done: an invalid end stays put, with its reason already showing. */
  confirmCustom(): void {
    if (!this.customValid) return;
    this.pickedCustom.emit({ endDate: this.customDate, endTime: this.customTime });
  }
}
